import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
      <div className="px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <span className="text-xl md:text-2xl font-heading font-bold text-foreground-950 tracking-tight">
                Vigorbuy
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 rounded-lg text-sm font-label font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Home
            </Link>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-label font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Categories</span>
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-background-50 border border-background-200/70 rounded-xl shadow-sm overflow-hidden animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-[400px] overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm font-label text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/vigorbuy-spreadsheet-guide"
              className="px-4 py-2 rounded-lg text-sm font-label font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-lg text-sm font-label text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Vigorbuy Sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/70 animate-[fadeIn_0.15s_ease-out]">
            <div className="pt-3 space-y-1">
              <Link
                to="/"
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-label font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-home-4-line"></i>
                </span>
                Home
              </Link>

              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-label font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                    <i className="ri-grid-line"></i>
                  </span>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="ml-3 pl-3 border-l-2 border-background-200 space-y-0.5 animate-[fadeIn_0.15s_ease-out]">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2 text-sm font-label text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <hr className="my-2 border-background-200/70" />

              <Link
                to="/vigorbuy-spreadsheet-guide"
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-label font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                  <i className="ri-book-open-line"></i>
                </span>
                Shopping Guide
              </Link>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-label text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                  <i className="ri-external-link-line"></i>
                </span>
                Vigorbuy Sheet
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block px-3 py-3 mt-2 text-sm font-label font-semibold bg-primary-500 text-background-50 rounded-full text-center hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </nav>
  );
}